import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Quote, Eye, Target } from "lucide-react";
import { LazyImage } from "@/components/site/LazyImage";
import { Reveal, MaskReveal } from "@/components/site/Reveal";
import { Ribbon } from "@/components/site/Ribbon";
import { IMAGES } from "@/config/images.config";

const MILESTONES = [
  { year: "1980", title: "Hand Spinning & Handloom", text: "The journey begins with Balaji Bhummya Kyatam & Nirmala Balaji Kyatam — mastering hand spinning and handloom weaving, laying the family's textile foundation." },
  { year: "1997", title: "Power-Loom Manufacturing", text: "Shriniwas Balaji Kyatam scales the craft into power-loom manufacturing, marking the shift from artisanal weaving to organised production." },
  { year: "2015", title: "Bhargavrushi Textile", text: "Modern rapier looms are installed with a capacity of ~15–20 tonnes per month, alongside growing merchant-export experience and quality systems." },
  { year: "2026", title: "Brahmandnayak World Trade", text: "A new chapter — BWT is founded to take the family's textile legacy directly into global trade, export and international supply." },
];

const LEADERS = [
  { role: "Founder", name: "Shriniwas Balaji Kyatam" },
  { role: "Owner", name: "Bharti Shriniwas Kyatam" },
  { role: "Managing Director", name: "Bhargavram Shriniwas Kyatam" },
  { role: "Managing Director", name: "Rushabh Shriniwas Kyatam" },
];

function Timeline() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start center", "end center"] });
  const height = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <div ref={ref} className="relative mx-auto max-w-4xl" data-testid="timeline">
      {/* base line */}
      <div className="absolute left-5 top-0 h-full w-px bg-navy/10 md:left-1/2" />
      {/* fill line */}
      <motion.div style={{ height }} className="absolute left-5 top-0 w-px bg-sky md:left-1/2" />

      <div className="space-y-16 md:space-y-28">
        {MILESTONES.map((m, i) => {
          const left = i % 2 === 0;
          return (
            <div key={m.year} className="relative pl-16 md:pl-0" data-testid={`milestone-${m.year}`}>
              {/* dot */}
              <span className="absolute left-5 top-2 z-10 -translate-x-1/2 md:left-1/2">
                <span className="block h-4 w-4 rounded-full border-2 border-sky bg-neutral-50" />
              </span>
              <div className={`md:grid md:grid-cols-2 md:gap-16 ${left ? "" : ""}`}>
                <Reveal className={left ? "md:text-right md:pr-16" : "md:col-start-2 md:pl-16"}>
                  <span className="font-display text-5xl font-black tracking-tighter text-navy md:text-7xl">{m.year}</span>
                  <h3 className="mt-3 font-display text-xl font-bold tracking-tight text-sky md:text-2xl">{m.title}</h3>
                  <p className="mt-4 text-sm leading-relaxed text-navy/60 md:text-base">{m.text}</p>
                </Reveal>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function About() {
  return (
    <div data-testid="about-page">
      {/* HEADER */}
      <section className="relative overflow-hidden bg-navy px-6 pb-24 pt-40 text-white md:px-12 md:pb-32 md:pt-52 lg:px-16" data-testid="about-hero">
        <div className="absolute inset-0 opacity-25">
          <img src={IMAGES.about.hero} alt="Textile mill" className="h-full w-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/70 to-navy/40" />
        <div className="relative mx-auto max-w-[1600px]">
          <Reveal><p className="mb-6 font-display text-xs font-semibold uppercase tracking-[0.34em] text-sky md:text-sm">About Brahmandnayak World Trade</p></Reveal>
          <h1 className="max-w-5xl font-display text-4xl font-black leading-[0.95] tracking-tighter md:text-7xl lg:text-[5rem]">
            <MaskReveal lines={["A Legacy of Textile Craft.", "A New Journey in", "Global Trade."]} />
          </h1>
        </div>
      </section>

      <Ribbon />

      {/* TIMELINE */}
      <section className="px-6 py-24 md:px-12 md:py-32 lg:px-16 lg:py-40" data-testid="timeline-section">
        <div className="mx-auto max-w-[1600px]">
          <Reveal className="mb-20 text-center">
            <p className="font-display text-xs font-bold uppercase tracking-[0.24em] text-sky">Our Story</p>
            <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight text-navy md:text-6xl">Three generations of craft</h2>
          </Reveal>
          <Timeline />
        </div>
      </section>

      {/* LEGACY IMAGES */}
      <section className="mx-auto max-w-[1600px] px-6 pb-24 md:px-12 md:pb-32 lg:px-16" data-testid="legacy-section">
        <div className="grid gap-5 md:grid-cols-2">
          <Reveal>
            <LazyImage src={IMAGES.about.legacy1} alt="Brahmandnayak World Trade legacy" className="aspect-[4/3] rounded-3xl" imgClassName="hover:scale-105 transition-transform duration-700" />
            <p className="mt-4 font-display text-sm uppercase tracking-[0.2em] text-navy/50">Our Legacy · Rooted in Textile Craft</p>
          </Reveal>
          <Reveal delay={0.1}>
            <LazyImage src={IMAGES.about.legacy2} alt="Brahmandnayak World Trade journey" className="aspect-[4/3] rounded-3xl" imgClassName="hover:scale-105 transition-transform duration-700" />
            <p className="mt-4 font-display text-sm uppercase tracking-[0.2em] text-navy/50">Our Journey · From Our Family to the World</p>
          </Reveal>
        </div>
      </section>

      {/* LEADERSHIP */}
      <section className="bg-navy px-6 py-24 text-white md:px-12 md:py-32 lg:px-16 lg:py-40" data-testid="leadership-section">
        <div className="mx-auto max-w-[1600px]">
          <Reveal className="mb-16">
            <p className="font-display text-xs font-bold uppercase tracking-[0.24em] text-sky">Leadership</p>
            <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight md:text-6xl">The family behind BWT</h2>
          </Reveal>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {LEADERS.map((l, i) => (
              <Reveal key={l.name} delay={i * 0.08}>
                <div className="group h-full rounded-3xl border border-white/10 bg-white/[0.03] p-8 transition-colors duration-500 hover:border-sky/50 hover:bg-white/[0.06]" data-testid={`leader-${i}`}>
                  <span className="font-display text-xs font-bold uppercase tracking-[0.2em] text-sky">{l.role}</span>
                  <p className="mt-4 font-display text-2xl font-bold leading-tight tracking-tight">{l.name}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* VISION & MISSION */}
      <section className="mx-auto max-w-[1600px] px-6 pt-24 md:px-12 md:pt-32 lg:px-16" data-testid="vision-mission-section">
        <Reveal className="mb-14">
          <p className="font-display text-xs font-bold uppercase tracking-[0.24em] text-sky">Our Purpose</p>
          <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight text-navy md:text-6xl">Vision & Mission</h2>
        </Reveal>
        <div className="grid gap-6 md:grid-cols-2">
          <Reveal>
            <div className="h-full rounded-3xl border border-black/10 bg-white p-8 md:p-12" data-testid="vision-card">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-navy text-sky"><Eye className="h-6 w-6" /></span>
              <h3 className="mt-8 font-display text-2xl font-bold tracking-tight text-navy md:text-3xl">Our Vision</h3>
              <p className="mt-4 leading-relaxed text-navy/60">
                To become a globally trusted name in textile trade — carrying India's craftsmanship to markets worldwide, and setting a benchmark for quality, reliability and long-term partnerships across international and domestic trade.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="h-full rounded-3xl border border-black/10 bg-navy p-8 text-white md:p-12" data-testid="mission-card">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 text-sky"><Target className="h-6 w-6" /></span>
              <h3 className="mt-8 font-display text-2xl font-bold tracking-tight md:text-3xl">Our Mission</h3>
              <p className="mt-4 leading-relaxed text-white/65">
                To deliver premium, customized Terry Towel products through quality-focused sourcing and professional order coordination — making global and domestic trade simple, transparent and dependable for every buyer we serve.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* QUOTE BLOCK */}
      <section className="mx-auto max-w-[1600px] px-6 py-24 md:px-12 md:py-32 lg:px-16 lg:py-40" data-testid="quote-section">
        <Reveal>
          <Quote className="h-12 w-12 text-sky" />
          <p className="mt-8 font-display text-3xl font-black leading-[1.05] tracking-tighter text-navy md:text-6xl lg:text-7xl">
            Four Decades. Three Generations. One Global Vision.
          </p>
          <p className="mt-10 max-w-3xl font-display text-xl font-light leading-snug text-navy/70 md:text-2xl">
            Rooted in handloom heritage. Strengthened by manufacturing. Driven by the next generation. Connected to the world.
          </p>
          <p className="mt-10 font-display text-lg font-semibold uppercase tracking-[0.2em] text-sky">
            From Our Family's Hands to Global Markets.
          </p>
        </Reveal>
      </section>
    </div>
  );
}
